// React and core libraries
import * as React from "react";
import { useCallback, useContext } from "react";

// Third-party libraries
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridSortItem,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { OctagonAlert } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { snakeCase } from "lodash";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";
import { ArrayParam } from "serialize-query-params/src/params";

// Internal components
import { QueryFetchingIndicator } from "../../../components/system/QueryFetchingIndicator";
import { DataGridCard } from "../../../components/system/data-grid-card";
import { useScopedContext } from "../../../shared-components/ScopeSelector";
import { ScopeSelector } from "../../../components/system/scope-selector";

import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "../../../components/ui/alert";
import { Checkbox } from "../../../components/ui/checkbox";
import { Link } from "../../../components/ui/link";
import { Badge } from "../../../components/ui/badge";
import { Stack } from "../../../components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip";

// Internal utilities, hooks, and contexts
import { Urls } from "../../../domain/urls";
import { UserContext } from "../../../crud/user/context";
import { useInteractionFlag } from "../../../crud/interactions/hooks";
import { InteractionFlagNames } from "../../../crud/interactions/types";
import { useTags } from "../../../crud/tags/hooks";
import {
  useClusters,
  useStaffHasSeenMutation,
} from "../../../crud/cluster/hooks";
import { useUserMemberships } from "../../../crud/user/hooks";
import { formatSeconds } from "../../../utils";
import { useTimezone } from "../../../utils/hooks";
import {
  DeclarativeViewsClusterClusterListV3OrderByDirectionEnum,
  DeclarativeViewsClusterClusterListV3OrderByEnum,
  ListClusterV2,
  TagAlertHitSchema,
} from "../../../api-client";

// Relative imports (components from same directory)
import { ClusterTag } from "../types";
import { ClusterState } from "../Information/ClusterStateComponent";
import { MetricFlags } from "../MetricFlags";
import { levelToAlertSev } from "../Information/TagAlerts";
import { DashboardButton } from "../DashboardButton";
import { PauseButton, StopButton } from "../StopButton";
import { CloudBackendChip } from "../Information/CloudBackendChip";

// Overview-specific imports
import { ClusterSearchBar } from "./ClusterSearchBar";
import { ClusterAlertLevelFilter } from "./ClusterAlertLevelFilter";
import { ColumnsButton } from "./ColumnsButton";
import * as constants from "./constants";
import * as utils from "./utils";

const {
  LONG_TIME_FORMAT,
  RUNNING_STATES,
  STOPPED_STATES,
  NICE_TAG_NAMES,
  RUNNING_CLUSTERS_COLUMN_KEY,
  STOPPED_CLUSTERS_COLUMN_KEY,
  RUNNING_INITIAL_COLUMN_VISIBILITY,
  STOPPED_INITIAL_COLUMN_VISIBILITY,
} = constants;

const {
  showWorkerStatus,
  showWorkerSpotCount,
  prettyTagLabel,
  getClustersColumnVisibilityModel,
} = utils;

export const Overview = (): React.ReactElement => {
  const {
    user: { isStaff: isStaff, id: userId },
  } = useContext(UserContext);
  const [scope, setScope] = useScopedContext();
  const [offsetRunning, setOffsetRunning] = React.useState(0);
  const [limitRunning, setLimitRunning] = React.useState(10);
  const [offsetStopped, setOffsetStopped] = React.useState(0);
  const [limitStopped, setLimitStopped] = React.useState(10);
  const [runningPage, setRunningPage] = React.useState(0);
  const [stoppedPage, setStoppedPage] = React.useState(0);
  const [runningSort, setRunningSort] = React.useState<
    GridSortItem | undefined
  >();
  const [stoppedSort, setStoppedSort] = React.useState<
    GridSortItem | undefined
  >();

  const [clusterNameFilter, setClusterNameFilter] = React.useState<
    string | undefined
  >();

  const [tagFilters, setTagFilters] = useQueryParam(
    "tagFilters",
    withDefault(ArrayParam, []),
  );
  const [alertLevelFilter, setAlertLevelFilter] = useQueryParam(
    "alertLevel",
    withDefault(NumberParam, 0),
  );
  const [displayTz] = useTimezone();

  // endpoint to fetch clusters is pretty expensive, so for now only let staff try large number of clusters per page
  // TODO (future PR) separate endpoints for getting list (cheap) and getting details (expensive).
  //   Usually there's a large number of stopped clusters and we need to know when this list changes, but once
  //   we have details for a stopped cluster, there's no need to update the information, it's not changing.
  const PER_PAGE_OPTIONS = isStaff ? [10, 20, 50, 100] : [10, 20];
  const scopeFilters = {
    ...(scope.type === "account" ? { account: scope.name } : {}),
    ...(scope.type === "compound" ? { compoundScope: scope.name } : {}),
    ...(scope.type === "user" ? { user: scope.name } : {}),
    ...(scope.type === "organization" ? { organization: scope.name } : {}),
    ...(scope.type === "custom-staff-list"
      ? { customStaffScope: scope.id }
      : {}),
  };
  const {
    data: runningClusters,
    refetch: refetchRunningClusters,
    isLoading: runningClustersLoading,
  } = useClusters({
    offset: offsetRunning,
    limit: limitRunning,
    clusterName: clusterNameFilter ? clusterNameFilter : undefined,
    state: RUNNING_STATES,
    tags: tagFilters.length ? (tagFilters as string[] | undefined) : undefined,
    minAlertLevel: alertLevelFilter,
    orderBy: runningSort
      ? (snakeCase(
          runningSort?.field,
        ) as DeclarativeViewsClusterClusterListV3OrderByEnum)
      : undefined,
    orderByDirection: runningSort?.sort
      ? (snakeCase(
          runningSort?.sort,
        ) as DeclarativeViewsClusterClusterListV3OrderByDirectionEnum)
      : undefined,
    ...scopeFilters,
  });
  const {
    data: stoppedClusters,
    refetch: refetchStoppedClusters,
    isLoading: stoppedClustersLoading,
  } = useClusters({
    offset: offsetStopped,
    limit: limitStopped,
    clusterName: clusterNameFilter ? clusterNameFilter : undefined,
    state: STOPPED_STATES,
    tags: tagFilters.length ? (tagFilters as string[]) : undefined,
    minAlertLevel: alertLevelFilter,
    orderBy: stoppedSort
      ? (snakeCase(
          stoppedSort?.field,
        ) as DeclarativeViewsClusterClusterListV3OrderByEnum)
      : undefined,
    orderByDirection: stoppedSort?.sort
      ? (snakeCase(
          stoppedSort?.sort,
        ) as DeclarativeViewsClusterClusterListV3OrderByDirectionEnum)
      : undefined,
    ...scopeFilters,
  });

  const workerTooltipInfo = useCallback(
    (row: ListClusterV2) =>
      row.workerTypes.length > 0 ? (
        <Stack spacing={2}>
          {row.workerTypes.map((workerType) => (
            <div key={`${row.id}_${workerType.vmType}`}>
              {workerType.vmType} {workerType.vmType ? "(" : ""}
              {showWorkerStatus(workerType)},{showWorkerSpotCount(workerType)}
              {workerType.vmType ? ")" : ""}
              {row.workerAcceleratorType &&
                !!workerType.vmType &&
                ` with ${row.workerAcceleratorType} (${row.workerAcceleratorCount} per worker)`}
            </div>
          ))}
        </Stack>
      ) : (
        `No instances created`
      ),
    [
      /* No additional dependencies needed as showWorkerStatus and showWorkerSpotCount are stable */
    ],
  );

  const runningClustersApiRef = useGridApiRef();
  const stoppedClustersApiRef = useGridApiRef();

  // for non-staff, just show regular user tags; staff can see system tags (metrics, etc)
  const { data: tags, isLoading: isLoadingTags } = useTags({
    ...(scope.type === "compound" ? { compoundScope: scope.name } : {}),
    ...(scope.type === "account" ? { account: scope.name } : {}),
    ...(scope.type === "organization" ? { organization: scope.name } : {}),
    ...(scope.type === "user" ? { user: scope.name } : {}),
    systemTags: isStaff,
  });

  const tagColumnKeys = React.useMemo<string[]>(
    () => (tags || []).map((tag) => tag.label),
    [tags],
  );
  const tagColumnKeyFields = React.useMemo<string[]>(
    () => tagColumnKeys.map((label) => `tag_${label}`),
    [tagColumnKeys],
  );

  const tagColumns = React.useMemo<GridColDef<ListClusterV2>[]>(
    () =>
      tagColumnKeys.map((label) => ({
        // use "tag_" prefix to avoid collision with standard fields (e.g., "name" tag and cluster name field)
        field: `tag_${label}`,
        // we can't use flex because too many flex columns causes re-render infinite loop
        width: 40 + 8 * prettyTagLabel(label).length,
        sortable: false,
        type: "string",
        headerName: NICE_TAG_NAMES[label] || label,
        renderHeader: () => {
          return (
            <Tooltip>
              <TooltipTrigger>
                <span>{prettyTagLabel(label)}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{label}</p>
              </TooltipContent>
            </Tooltip>
          );
        },
        renderCell: (params: GridRenderCellParams) => {
          const tagValue = params.row.tagList.filter(
            (value: ClusterTag) => value.label === label,
          )[0]?.value;
          return tagValue ? (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline">{tagValue}</Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>{tagValue}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            ""
          );
        },
      })),
    [tagColumnKeys],
  );
  const { mutateAsync: staffMutateAsync } = useStaffHasSeenMutation();
  const staffColumns = React.useMemo<GridColDef<ListClusterV2>[]>(
    () =>
      isStaff
        ? [
            {
              field: "staffHasSeen",
              headerName: "Seen?",
              renderCell: (params: GridRenderCellParams<ListClusterV2>) => (
                <Checkbox
                  checked={params.row.staffHasSeen}
                  onCheckedChange={(checked) => {
                    staffMutateAsync({
                      account: params.row.accountSlug,
                      pk: params.row.id,
                      seen: checked === true,
                    });
                  }}
                />
              ),
            },
            {
              field: "organizationFeatureTier",
              sortable: false,
              headerName: "Tier",
              width: 60,
              renderCell: (params) => (
                <Tooltip>
                  <TooltipTrigger>
                    <Badge
                      variant={
                        params.row.organizationFeatureTier === "professional"
                          ? "default"
                          : "outline"
                      }
                    >
                      {params.row.organizationFeatureTier?.slice(0, 3)}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{params.row.organizationFeatureTier}</p>
                  </TooltipContent>
                </Tooltip>
              ),
            },
            {
              field: "dealStageGroup",
              sortable: false,
              headerName: "Stage",
              renderCell: (params) =>
                params.row.dealStageGroup ? (
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge
                        style={{
                          color: params.row.dealStageGroup?.color.includes(
                            "dark",
                          )
                            ? "white"
                            : "black",
                          backgroundColor: params.row.dealStageGroup?.color,
                        }}
                      >
                        {params.row.dealStageGroup?.name}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{params.row.dealStageGroup?.name}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : null,
            },
          ]
        : [],
    [staffMutateAsync, isStaff],
  );
  const getColumns = useCallback(
    (state: string): GridColDef<ListClusterV2>[] => [
      ...staffColumns,
      {
        field: "backend",
        sortable: false,
        headerName: "Backend",
        renderCell: (params) => (
          <CloudBackendChip
            hosted={params.row.coiledHosted}
            backend={params.row.backendType}
          />
        ),
      },
      {
        field: "id",
        headerName: "ID",
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        hideable: false,
        sortable: false,
        renderCell: (params) => (
          <Link
            variant="inherit"
            to={`/${Urls.Clusters}/${params.row.id}/account/${params.row.accountSlug}/information`}
          >
            {params.row.name}
          </Link>
        ),
      },
      {
        field: "creatorUsername",
        headerName: "Creator",
        sortable: false,
        renderCell: (params) => {
          return (
            <>
              {scope.type !== "account" && (
                <>
                  <Link
                    key={`account-scope-link-${params.row.accountSlug}`}
                    variant="inherit"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setScope({
                        type: "account",
                        name: params.row.accountName,
                        slug: params.row.accountSlug,
                        id: params.row.accountId,
                      });
                    }}
                  >
                    {params.row.accountSlug}
                  </Link>
                  /
                </>
              )}
              <Link
                key={`user-scope-link-${params.row.creatorUsername}`}
                variant="inherit"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setScope({
                    type: "user",
                    name: params.row.creatorUsername,
                    id: params.row.creatorId,
                  });
                }}
              >
                {params.row.creatorUsername}
              </Link>
            </>
          );
        },
        flex: 0.7,
      },
      {
        field: "state",
        headerName: "Status",
        sortable: false,
        width: 90,
        renderCell: (params) => (
          <ClusterState
            state={{ state: params.row.state, reason: params.row.reason }}
          />
        ),
      },
      {
        field: "workers",
        headerName: "Workers",
        sortable: false,
        flex: 0.7,
        renderCell: (params) => (
          <Tooltip>
            <TooltipTrigger>
              <span>
                {params.row.runningWorkers}/{params.row.desiredWorkers}
                {params.row.workerTypes.filter(
                  (item) =>
                    item.errorCount?.quota ||
                    item.errorCount?.availability ||
                    item.errorCount?.other,
                ).length > 0 ? (
                  <OctagonAlert className="h-4 w-4 text-red-500 inline align-middle" />
                ) : (
                  <></>
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>{workerTooltipInfo(params.row)}</TooltipContent>
          </Tooltip>
        ),
      },
      {
        field: "totalWorkers",
        headerName: "Total Workers",
        sortable: false,
        width: 120,
        renderCell: (params) => (
          <Tooltip>
            <TooltipTrigger>
              <span>
                {params.row.totalWorkers}
                {params.row.workerTypes.filter(
                  (item) =>
                    item.errorCount?.quota ||
                    item.errorCount?.availability ||
                    item.errorCount?.other,
                ).length > 0 ? (
                  <OctagonAlert className="h-4 w-4 text-red-500 inline align-middle" />
                ) : (
                  <></>
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>{workerTooltipInfo(params.row)}</TooltipContent>
          </Tooltip>
        ),
      },
      {
        field: "softwareEnvironmentName",
        flex: 1,
        sortable: false,
        headerName: "Software Environment",
        renderCell: (params) => {
          if (params.row.senvAliasId) {
            const link = params.row.senvBuildId
              ? `/${Urls.Software}/alias/${params.row.senvAliasId}/build/${params.row.senvBuildId}?account=${params.row.accountSlug}`
              : `/${Urls.Software}/alias/${params.row.senvAliasId}?account=${params.row.accountSlug}`;
            return (
              <Link variant="inherit" key={link} to={link}>
                {params.row.softwareEnvironmentName}
              </Link>
            );
          }
          return params.row.softwareEnvironmentName || "Unknown";
        },
        valueGetter: (params) =>
          params.row.softwareEnvironmentName
            ? params.row.softwareEnvironmentName
            : "Package Sync",
      },
      {
        field: "region",
        headerName: "Region",
        sortable: false,
        flex: 1,
        valueGetter: (params) => params.row.regionName,
      },
      {
        field: "lastSeen",
        headerName: "Last Seen",
        sortable: false,
        flex: 1,
        renderCell: (params) => (
          <Tooltip>
            <TooltipTrigger>
              <span>
                {params.row.lastSeen
                  ? formatDistanceToNow(new Date(params.row.lastSeen), {
                      addSuffix: true,
                    })
                  : `never`}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {params.row.lastSeen
                ? formatInTimeZone(
                    new Date(params.row.lastSeen),
                    displayTz,
                    LONG_TIME_FORMAT,
                  )
                : `No last seen time on this cluster`}
            </TooltipContent>
          </Tooltip>
        ),
      },
      {
        field: "billableTime",
        type: "string",
        sortable: false,
        headerName: "Uptime",
        valueFormatter: ({ value }) =>
          value ? formatSeconds(value, 0, "short") : "",
        width: 100,
      },
      {
        field: "approximateCloudTotalCost",
        headerName: "Approx Cloud Cost",
        type: state === "stopped" ? "number" : "string",
        // valueFormatter: ({ value }) => (value ? `$${value.toFixed(2)}` : ""),
        renderCell: (params) => {
          const isRunningClustersGrid =
            params.api.rootElementRef?.current?.className.includes(
              "running-clusters-grid",
            );
          const totalCost = params.row.approximateCloudTotalCost?.toFixed(2);
          const totalCostFour =
            params.row.approximateCloudTotalCost?.toFixed(4);
          const costPerHour =
            params.row.approximateCloudCostPerHour?.toFixed(2);

          const costFormula =
            params.row.approximateCloudCostVersion === 1
              ? "Cloud cost estimated using price for the typical US region, and uses spot discount rate of 50%."
              : "Cloud cost estimated at $0.05/CPU-hour.";
          const otherCostFactors =
            params.row.approximateCloudCostVersion === 1
              ? "Actual costs vary by region and by spot discount rate."
              : "It does not account for variance between instances types, regions, or spot pricing.";

          return totalCost ? (
            <Tooltip>
              <TooltipTrigger>
                <div>
                  {isRunningClustersGrid ? (
                    <span>
                      ${costPerHour}/hr (${totalCost} so far){" "}
                    </span>
                  ) : (
                    <span>${totalCost}</span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div>Total Cloud Compute Cost: ${totalCostFour}</div>
                {isRunningClustersGrid && costPerHour ? (
                  <div>Current $/Hour: ${costPerHour}/hour</div>
                ) : null}
                <div>
                  <br />
                  {costFormula} This gives a <b>very rough approximation</b> of
                  your cloud provider costs. {otherCostFactors} Only compute
                  costs are included, not network egress costs. For accurate
                  cost values see billing information from your cloud provider.
                </div>
                <div>
                  <br />
                  Beyond what you owe your cloud provider, Coiled does not
                  charge for usage until you exceed the CPU-hours included in
                  your plan.
                </div>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger>
                <div>&mdash;</div>
              </TooltipTrigger>
              <TooltipContent>
                No cloud cost estimate for this cluster.
              </TooltipContent>
            </Tooltip>
          );
        },
        flex: 1,
        sortable: false,
      },
      {
        field: "totalCost",
        type: state === "stopped" ? "number" : "string",
        sortable: state === "stopped",
        headerName: "Used Credits",
        renderCell: (params) => {
          const isRunningClustersGrid =
            params.api.rootElementRef?.current?.className.includes(
              "running-clusters-grid",
            );
          const totalCost = params.row.totalCost?.toFixed(2);
          const costPerHour = params.row.costPerHour?.toFixed(2);
          return (
            <Tooltip>
              <TooltipTrigger>
                <div>
                  {isRunningClustersGrid ? (
                    <span>
                      {params.row.costPerHour}/hr ({totalCost} so far){" "}
                    </span>
                  ) : (
                    <span>{totalCost}</span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div>Total Used Credits: {totalCost}</div>
                {isRunningClustersGrid && params.row.costPerHour ? (
                  <div>Current Credits/Hr: {costPerHour}</div>
                ) : null}
              </TooltipContent>
            </Tooltip>
          );
        },
        flex: 1,
      },
      {
        field: "nTasks",
        sortable: true,
        type: "number",
        headerName: "Tasks",
        width: 100,
      },
      ...tagColumns,
      {
        field: "flags",
        headerName: "Flags",
        sortable: false,
        flex: 1,
        renderCell: (params) => (
          <MetricFlags metricFlags={params.row.metricFlags} />
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 80,
        getActions: (params: GridRowParams<ListClusterV2>) => {
          const isNotebook = params.row?.clusterType === "notebook";
          const isPrivate =
            isNotebook &&
            params.row.privateToCreator &&
            params.row.creatorId !== userId &&
            !isStaff;

          return [
            <DashboardButton
              key={`dashboard-button-${params.row.id}`}
              isNotebook={isNotebook}
              schedulerState={params.row.schedulerState}
              dashboardAddress={params.row.dashboardAddress}
              isPrivate={isPrivate}
              iconOnly
            />,
            ...(params.row?.pauseOnExit
              ? [
                  <PauseButton
                    key={`pause-button-${params.row.id}`}
                    clusterId={params.row.id}
                    accountSlug={params.row.accountSlug}
                    clusterCreatorId={params.row.creatorId}
                    clusterCreatorName={params.row.creatorUsername}
                    isNotebook={isNotebook}
                    clusterState={params.row.state}
                    pauseOnExit
                    iconOnly
                  />,
                ]
              : [
                  <StopButton
                    key={`stop-button-${params.row.id}`}
                    clusterId={params.row.id}
                    accountSlug={params.row.accountSlug}
                    clusterCreatorId={params.row.creatorId}
                    clusterCreatorName={params.row.creatorUsername}
                    isNotebook={isNotebook}
                    clusterState={params.row.state}
                    pauseOnExit={false}
                    iconOnly
                  />,
                ]),
          ];
        },
      },
    ],
    [
      staffColumns,
      tagColumns,
      scope.type,
      setScope,
      workerTooltipInfo,
      displayTz,
    ],
  );

  const runningClustersColumns = React.useMemo<GridColDef<ListClusterV2>[]>(
    () => getColumns("running"),
    [getColumns],
  );

  const stoppedClustersColumns = React.useMemo<GridColDef<ListClusterV2>[]>(
    () => getColumns("stopped"),
    [getColumns],
  );

  const hiddenFields = ["__detail_panel_toggle__"];
  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);
  };
  const getAlertListAsDetails = (row: GridRowParams<any>) => {
    const alertsToShow = (row.row.alertList || []).filter(
      (alert: TagAlertHitSchema) => isStaff || !alert.staffOnly,
    );
    return alertsToShow.length > 0 ? (
      <Stack
        style={{
          padding: ".5rem 1rem",
          backgroundColor: "white",
          borderBottom: "1px solid rgba(224, 224, 224, 1)",
        }}
        spacing={1}
      >
        {alertsToShow.map((hit: TagAlertHitSchema) => (
          <Alert
            variant={levelToAlertSev(hit.level)}
            key={`${row.row.id}_${hit.title}`}
          >
            <b>{hit.title}</b> on cluster <b>{row.row.name}</b>
          </Alert>
        ))}
      </Stack>
    ) : null;
  };

  // Check if any rows have alerts to show
  const hasRunningAlerts = React.useMemo(() => {
    return (runningClusters?.items || []).some((cluster) => {
      const alertsToShow = (cluster.alertList || []).filter(
        (alert: TagAlertHitSchema) => isStaff || !alert.staffOnly,
      );
      return alertsToShow.length > 0;
    });
  }, [runningClusters?.items, isStaff]);

  const hasStoppedAlerts = React.useMemo(() => {
    return (stoppedClusters?.items || []).some((cluster) => {
      const alertsToShow = (cluster.alertList || []).filter(
        (alert: TagAlertHitSchema) => isStaff || !alert.staffOnly,
      );
      return alertsToShow.length > 0;
    });
  }, [stoppedClusters?.items, isStaff]);

  const runningClustersColumnsButtonRef = React.useRef<HTMLButtonElement>(null);
  const stoppedClustersColumnsButtonRef = React.useRef<HTMLButtonElement>(null);
  // wait till tags are loaded since we add columns once tags are loaded
  // but these aren't included in initialState.columns.columnVisibilityModel
  // so they'll be shown by default, which isn't what we want
  // TODO don't use initialState for default column visibility

  const hasMadeACluster = useInteractionFlag(
    InteractionFlagNames.HasCreatedCluster,
  );

  // Only show dialogue directing users back to Getting Started if:
  // 1. User hasn't created a cluster (based on interaction flag)
  // 2. Data has finished loading for both running and stopped clusters
  // 3. There are no clusters in either list
  const hasCreatedCluster = hasMadeACluster.data?.completed;
  const dataIsLoaded = !runningClustersLoading && !stoppedClustersLoading;
  const hasRunningItems = runningClusters?.items !== undefined;
  const hasStoppedItems = stoppedClusters?.items !== undefined;
  const allItemsEmpty =
    hasRunningItems &&
    runningClusters.items.length === 0 &&
    hasStoppedItems &&
    stoppedClusters.items.length === 0;

  const { data: memberships } = useUserMemberships();
  const inMultipleWorkspacesOrWorkspacesNotLoaded =
    memberships == null ? true : memberships.length > 1;

  // let's be pretty conservative about when to show this dialogue forcing users back to "Getting Started":
  // it'd be very bad to refuse to show this page when it actually has something they want to see!
  //
  // in particular, let's just never show the dialogue if they're in multiple workspaces
  const showGettingStartedDialogue =
    !inMultipleWorkspacesOrWorkspacesNotLoaded &&
    !hasCreatedCluster &&
    dataIsLoaded &&
    hasRunningItems &&
    hasStoppedItems &&
    allItemsEmpty;

  return isLoadingTags ? (
    <></>
  ) : (
    <>
      <Stack>
        {showGettingStartedDialogue && (
          <Alert severity="warning">
            <AlertTitle>No clusters</AlertTitle>
            <AlertDescription>
              This page shows all of your clusters, including hardware metrics,
              logs, cloud costs, and more. To get started,{" "}
              <Link href="/get-started">launch a cluster</Link>.
            </AlertDescription>
          </Alert>
        )}
        {/* Page controls */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="between"
          alignItems="center"
        >
          <ClusterSearchBar
            onNameFilterChange={(name) => {
              if (clusterNameFilter !== name) {
                setClusterNameFilter(name);
                setRunningPage(0);
                setOffsetRunning(0);
                setStoppedPage(0);
                setOffsetStopped(0);
              }
            }}
            tagFilters={tagFilters}
            setTagFilters={setTagFilters}
          />
          {
            <ClusterAlertLevelFilter
              value={alertLevelFilter}
              setValue={setAlertLevelFilter}
            />
          }
          <ScopeSelector />
        </Stack>
        {/* Running clusters grid */}
        <DataGridCard
          title="Running Clusters"
          action={
            <div className="flex items-center gap-2">
              <ColumnsButton
                apiRef={runningClustersApiRef}
                columns={runningClustersColumns}
                storageKey={RUNNING_CLUSTERS_COLUMN_KEY}
                initialVisibilityModel={getClustersColumnVisibilityModel(
                  RUNNING_CLUSTERS_COLUMN_KEY,
                  RUNNING_INITIAL_COLUMN_VISIBILITY,
                  tagColumnKeyFields,
                )}
                onColumnVisibilityChange={(model) => {
                  localStorage.setItem(
                    RUNNING_CLUSTERS_COLUMN_KEY,
                    JSON.stringify(model),
                  );
                }}
                ref={runningClustersColumnsButtonRef}
              />
              <QueryFetchingIndicator
                query="clusterList"
                onClick={refetchRunningClusters}
              />
            </div>
          }
          dataGridProps={{
            apiRef: runningClustersApiRef,
            className: "running-clusters-grid",
            rowCount: runningClusters?.count || 0,
            disableColumnFilter: true,
            autoHeight: true,
            hideFooter: !runningClusters?.count,
            loading: runningClustersLoading,
            pagination: true,
            paginationMode: "server",
            sortingMode: "server",
            sx: { "--DataGrid-overlayHeight": "50px" },
            onSortModelChange: (model) => setRunningSort(model?.[0]),
            pageSizeOptions: PER_PAGE_OPTIONS,
            paginationModel: {
              page: runningPage,
              pageSize: limitRunning,
            },
            onPaginationModelChange: (model) => {
              setRunningPage(model.page);
              setLimitRunning(model.pageSize);
              setOffsetRunning(model.page * model.pageSize);
            },
            onColumnVisibilityModelChange: (model) => {
              localStorage.setItem(
                RUNNING_CLUSTERS_COLUMN_KEY,
                JSON.stringify(model),
              );
            },

            localeText: {
              noRowsLabel: "No running clusters match current filters",
            },
            slotProps: {
              panel: {
                anchorEl: runningClustersColumnsButtonRef.current,
                placement: "bottom-end",
              },
              columnsPanel: {
                getTogglableColumns,
              },
            },
            ...(hasRunningAlerts && {
              detailPanelExpandedRowIds: (runningClusters?.items || [])
                .filter((row: ListClusterV2) => row.alertList.length > 0)
                .map((row: ListClusterV2) => row.id),
              getDetailPanelContent: getAlertListAsDetails,
              getDetailPanelHeight: () => "auto",
            }),
            columns: runningClustersColumns,
            rows: runningClusters?.items || [],
            initialState: {
              columns: {
                columnVisibilityModel: getClustersColumnVisibilityModel(
                  RUNNING_CLUSTERS_COLUMN_KEY,
                  RUNNING_INITIAL_COLUMN_VISIBILITY,
                  tagColumnKeyFields,
                ),
              },
            },
          }}
        />
        {/* Stopped clusters grid */}
        <DataGridCard
          title="Stopped Clusters"
          action={
            <div className="flex items-center gap-2">
              <ColumnsButton
                apiRef={stoppedClustersApiRef}
                columns={stoppedClustersColumns}
                storageKey={STOPPED_CLUSTERS_COLUMN_KEY}
                initialVisibilityModel={getClustersColumnVisibilityModel(
                  STOPPED_CLUSTERS_COLUMN_KEY,
                  STOPPED_INITIAL_COLUMN_VISIBILITY,
                  tagColumnKeyFields,
                )}
                onColumnVisibilityChange={(model) => {
                  localStorage.setItem(
                    STOPPED_CLUSTERS_COLUMN_KEY,
                    JSON.stringify(model),
                  );
                }}
                ref={stoppedClustersColumnsButtonRef}
              />
              <QueryFetchingIndicator
                query="clusterList"
                onClick={refetchStoppedClusters}
              />
            </div>
          }
          dataGridProps={{
            apiRef: stoppedClustersApiRef,
            className: "stopped-clusters-grid",
            rowCount: stoppedClusters?.count || 0,
            loading: stoppedClustersLoading,
            disableColumnFilter: true,
            autoHeight: true,
            hideFooter: !stoppedClusters?.count,
            pagination: true,
            paginationMode: "server",
            sortingMode: "server",
            sx: { "--DataGrid-overlayHeight": "50px" },
            onSortModelChange: (model) => setStoppedSort(model?.[0]),
            pageSizeOptions: PER_PAGE_OPTIONS,
            paginationModel: {
              page: stoppedPage,
              pageSize: limitStopped,
            },
            onPaginationModelChange: (model) => {
              setStoppedPage(model.page);
              setLimitStopped(model.pageSize);
              setOffsetStopped(model.page * model.pageSize);
            },
            onColumnVisibilityModelChange: (model) => {
              localStorage.setItem(
                STOPPED_CLUSTERS_COLUMN_KEY,
                JSON.stringify(model),
              );
            },

            localeText: {
              noRowsLabel: "No stopped clusters match current filters",
            },
            slotProps: {
              panel: {
                anchorEl: stoppedClustersColumnsButtonRef.current,
                placement: "bottom-end",
              },
              columnsPanel: {
                getTogglableColumns,
              },
            },
            ...(hasStoppedAlerts && {
              detailPanelExpandedRowIds: (stoppedClusters?.items || [])
                .filter((row: ListClusterV2) => row.alertList.length > 0)
                .map((row: ListClusterV2) => row.id),
              getDetailPanelContent: getAlertListAsDetails,
              getDetailPanelHeight: () => "auto",
            }),
            columns: stoppedClustersColumns,
            rows: stoppedClusters?.items || [],
            initialState: {
              columns: {
                columnVisibilityModel: getClustersColumnVisibilityModel(
                  STOPPED_CLUSTERS_COLUMN_KEY,
                  STOPPED_INITIAL_COLUMN_VISIBILITY,
                  tagColumnKeyFields,
                ),
              },
              detailPanel: {
                expandedRowIds: (stoppedClusters?.items || [])
                  .filter((row: ListClusterV2) => row.alertList.length > 0)
                  .map((row: ListClusterV2) => row.id),
              },
            },
          }}
        />
      </Stack>
    </>
  );
};
